/**
 * Team API Service
 * Handles all team-related API calls including membership management and statistics
 */

import { apiClient } from "./apiService";

// Authentication is handled via HttpOnly cookies - no need to manually inject tokens

class TeamService {
  // ===== TEAM BROWSING & DISCOVERY =====

  /**
   * Get all teams with filtering and pagination
   * @param {Object} filters - Filter options
   * @param {string} filters.organizationId - Filter by organization
   * @param {string} filters.sport - Filter by sport type
   * @param {string} filters.status - Filter by team status
   * @param {string} filters.search - Search by team name
   * @param {number} filters.limit - Number of results per page
   * @param {number} filters.offset - Pagination offset
   */
  async getTeams(filters = {}) {
    try {
      const response = await apiClient.get("/teams", { params: filters });
      return response.data;
    } catch (error) {
      console.error("Error fetching teams:", error);
      throw error;
    }
  }

  /**
   * Get detailed team information including members and statistics
   * @param {number} teamId - Team ID
   */
  async getTeamDetails(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching team details for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Get basic team information
   * @param {number} teamId - Team ID
   */
  async getTeam(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching team ${teamId}:`, error);
      throw error;
    }
  }

  // ===== USER TEAM MANAGEMENT =====

  /**
   * Get current user's teams
   * @param {Object} filters - Filter options
   * @param {string} filters.status - Filter by membership status
   * @param {string} filters.role - Filter by user role
   * @param {number} filters.limit - Number of results per page
   * @param {number} filters.offset - Pagination offset
   */
  async getUserTeams(filters = {}) {
    try {
      const response = await apiClient.get("/teams/my-teams", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching user teams:", error);
      throw error;
    }
  }

  /**
   * Create a new team
   * @param {Object} teamData - Team creation data
   * @param {string} teamData.name - Team name
   * @param {string} teamData.description - Team description
   * @param {string} teamData.sport_type - Sport type
   * @param {string} teamData.organization - Organization name
   * @param {string} teamData.location - Team location
   * @param {string} teamData.logo_url - Team logo URL
   * @param {boolean} teamData.is_public - Whether team is public
   * @param {boolean} teamData.requires_approval - Whether team requires approval to join
   * @param {number} teamData.max_members - Maximum number of members
   * @param {Object} teamData.rules - Team rules and settings
   */
  async createTeam(teamData) {
    try {
      const response = await apiClient.post("/teams", teamData);
      return response.data;
    } catch (error) {
      console.error("Error creating team:", error);
      throw error;
    }
  }

  /**
   * Update team information
   * @param {number} teamId - Team ID to update
   * @param {Object} teamData - Updated team data
   */
  async updateTeam(teamId, teamData) {
    try {
      const response = await apiClient.put(`/teams/${teamId}`, teamData);
      return response.data;
    } catch (error) {
      console.error("Error updating team:", error);
      throw error;
    }
  }

  /**
   * Delete a team
   * @param {number} teamId - Team ID to delete
   */
  async deleteTeam(teamId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting team:", error);
      throw error;
    }
  }

  /**
   * Submit a request to join a team
   * @param {number} teamId - Team ID to join
   * @param {Object} requestData - Join request data
   * @param {string} requestData.requestedRole - Requested role (optional)
   * @param {string} requestData.message - Message to team managers (optional)
   */
  async joinTeam(teamId, requestData = {}) {
    try {
      const response = await apiClient.post(
        `/teams/${teamId}/join`,
        requestData
      );
      return response.data;
    } catch (error) {
      console.error(`Error joining team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Leave a team or remove a member (if authorized)
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID to remove
   */
  async removeTeamMember(teamId, userId) {
    try {
      const response = await apiClient.delete(
        `/teams/${teamId}/members/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error removing member ${userId} from team ${teamId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Alias for removeTeamMember - Remove a member from a team
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID to remove
   */
  async removeMember(teamId, userId) {
    return this.removeTeamMember(teamId, userId);
  }

  /**
   * Get team members list
   * @param {number} teamId - Team ID
   * @param {Object} filters - Filter options
   */
  async getTeamMembers(teamId, filters = {}) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/members`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching team members for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Get pending join requests for a team
   * @param {number} teamId - Team ID
   */
  async getJoinRequests(teamId) {
    try {
      const response = await apiClient.get(
        `/teams/${teamId}/members?status=pending`
      );
      const members =
        response?.data?.data?.members ||
        response?.data?.members ||
        response?.data?.data ||
        response?.data ||
        [];
      return Array.isArray(members) ? members : [];
    } catch (error) {
      console.error(`Error fetching join requests for team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Approve a join request
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID of the requester
   * @param {string} responseMessage - Optional message
   * @param {string|null} memberType - "primary" / "substitute"; omitted lets the
   *   server place the player as a starter if there is room, else as a sub
   */
  async approveJoinRequest(teamId, userId, responseMessage = "", memberType = null) {
    try {
      const body = { action: "approve", responseMessage };
      if (memberType) {
        body.memberType = memberType;
      }
      const response = await apiClient.put(
        `/teams/${teamId}/members/${userId}/status`,
        body
      );
      return response.data;
    } catch (error) {
      console.error(`Error approving join request:`, error);
      throw error;
    }
  }

  /**
   * Reject a join request
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID of the requester
   * @param {string} responseMessage - Optional message
   */
  async rejectJoinRequest(teamId, userId, responseMessage = "") {
    try {
      const response = await apiClient.put(
        `/teams/${teamId}/members/${userId}/status`,
        {
          action: "reject",
          responseMessage
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error rejecting join request:`, error);
      throw error;
    }
  }

  /**
   * Leave a team (soft delete - set status to inactive)
   * @param {number} teamId - Team ID
   */
  async leaveTeam(teamId) {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user.id;

      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Removing yourself is the leave action: the server marks the
      // membership inactive. Status can't be edited directly.
      const response = await apiClient.delete(
        `/teams/${teamId}/members/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error leaving team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Rejoin a team. This is a fresh join request: the server reactivates the
   * old membership, or files it as pending when the team requires approval.
   * @param {number} teamId - Team ID
   */
  async rejoinTeam(teamId) {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user.id;

      if (!userId) {
        throw new Error("User not authenticated");
      }

      const response = await apiClient.post(`/teams/${teamId}/join`, {
        requestedRole: "player",
      });
      return response.data;
    } catch (error) {
      console.error(`Error rejoining team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Update team member details
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID to update
   * @param {Object} updateData - Update data
   * @param {number} updateData.jerseyNumber - Jersey number
   * @param {string} updateData.position - Playing position
   * @param {string} updateData.notes - Additional notes
   */
  // ===== TEAM LOGO =====

  /** Upload or replace the team's logo; image is a data URL (see utils/imageResize). */
  async uploadTeamLogo(teamId, image) {
    const response = await apiClient.put(`/teams/${teamId}/logo`, { image });
    return response.data;
  }

  async removeTeamLogo(teamId) {
    const response = await apiClient.delete(`/teams/${teamId}/logo`);
    return response.data;
  }

  async updateTeamMember(teamId, userId, updateData) {
    try {
      const response = await apiClient.put(
        `/teams/${teamId}/members/${userId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating member ${userId} in team ${teamId}:`,
        error
      );
      throw error;
    }
  }

  // ===== TEAM INVITATIONS =====

  /**
   * Invite a user to join a team
   * @param {number} teamId - Team ID
   * @param {Object} invitationData - Invitation data
   * @param {number} invitationData.userId - User ID to invite
   * @param {string} invitationData.requestedRole - Role to assign (optional, default: 'player')
   * @param {string} invitationData.message - Message to the user (optional)
   */
  async inviteUserToTeam(teamId, invitationData) {
    try {
      const response = await apiClient.post(
        `/teams/${teamId}/invite`,
        invitationData
      );
      return response.data;
    } catch (error) {
      console.error(`Error inviting user to team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Get invitations received by the current user
   * @param {string} status - Filter by status (optional, default: 'pending')
   */
  async getUserInvitations(status = 'pending') {
    try {
      const response = await apiClient.get(
        `/teams/invitations`,
        {
          params: { status },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user invitations:', error);
      throw error;
    }
  }

  /**
   * Get invitations sent by a team
   * @param {number} teamId - Team ID
   * @param {string} status - Filter by status (optional, default: 'pending')
   */
  async getTeamInvitations(teamId, status = 'pending') {
    try {
      const response = await apiClient.get(
        `/teams/${teamId}/invitations`,
        {
          params: { status },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching team invitations for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Accept a team invitation
   * @param {number} invitationId - Invitation ID
   */
  async acceptInvitation(invitationId) {
    try {
      const response = await apiClient.put(
        `/teams/invitations/${invitationId}`,
        { action: 'accept' }
      );
      return response.data;
    } catch (error) {
      console.error(`Error accepting invitation ${invitationId}:`, error);
      throw error;
    }
  }

  /**
   * Decline a team invitation
   * @param {number} invitationId - Invitation ID
   * @param {string} message - Optional decline message
   */
  async declineInvitation(invitationId, message = '') {
    try {
      const response = await apiClient.put(
        `/teams/invitations/${invitationId}`,
        { action: 'decline', message }
      );
      return response.data;
    } catch (error) {
      console.error(`Error declining invitation ${invitationId}:`, error);
      throw error;
    }
  }

  // ===== TEAM MANAGEMENT (Coach/Manager only) =====

  /**
   * Process a team join request (approve/reject)
   * @param {number} teamId - Team ID
   * @param {number} userId - User ID of the request
   * @param {Object} reviewData - Review decision data
   * @param {string} reviewData.status - 'approved' or 'rejected'
   * @param {string} reviewData.responseMessage - Response message (optional)
   * @param {string} reviewData.assignedRole - Assigned role if approved (optional)
   * @param {number} reviewData.jerseyNumber - Jersey number if approved (optional)
   */
  async processMembershipRequest(teamId, userId, reviewData) {
    try {
      const response = await apiClient.put(
        `/teams/${teamId}/members/${userId}/status`,
        reviewData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error processing membership request for user ${userId} in team ${teamId}:`,
        error
      );
      throw error;
    }
  }

  // ===== TEAM STATISTICS =====

  /**
   * Get team statistics
   * @param {number} teamId - Team ID
   * @param {Object} filters - Filter options
   * @param {number} filters.season - Filter by season year
   * @param {string} filters.sport - Filter by sport type
   * @param {number} filters.limit - Limit number of results
   */
  async getTeamStats(teamId, filters = {}) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/stats`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching team stats for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Matches the team played or will play (ST-B05: the stats dashboard called
   * this but it did not exist).
   * GET /teams/:teamId/matches?limit=&status= → { data: { matches: [...] } }
   * @param {number} teamId - Team ID
   * @param {Object} filters - { limit, status }
   */
  async getTeamMatches(teamId, filters = {}) {
    const params = typeof filters === "object" && filters !== null ? filters : {};
    try {
      const response = await apiClient.get(`/teams/${teamId}/matches`, {
        params: { limit: 50, ...params },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching team matches for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Player leaderboard built from per-match player stats (ST-B07).
   * GET /teams/leaderboards?sportId=&metric=&limit=
   */
  async getLeaderboards(filters = {}) {
    try {
      const response = await apiClient.get(`/teams/leaderboards`, { params: filters });
      return response.data;
    } catch (error) {
      console.error("Error fetching leaderboards:", error);
      throw error;
    }
  }

  /**
   * Get team's scheduled/upcoming matches
   * @param {number} teamId - Team ID
   * @param {Object} filters - Query filters
   * @param {string} filters.status - Match status (default: 'scheduled')
   * @param {number} filters.limit - Number of matches to return (default: all)
   */
  async getTeamSchedule(teamId, filters = {}) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/schedule`, {
        params: { limit: 999, ...filters },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching team schedule for ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Get seasonal team statistics
   * @param {number} teamId - Team ID
   * @param {number} year - Season year (e.g., 2024)
   */
  async getSeasonStats(teamId, year) {
    try {
      const response = await apiClient.get(
        `/teams/${teamId}/stats/season/${year}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching season stats for team ${teamId}, year ${year}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get top performing players
   * @param {number} teamId - Team ID
   * @param {Object} options - Query options
   * @param {string} options.metric - Metric to rank by ('points', 'assists', 'performance_rating')
   * @param {number} options.limit - Number of top players to return
   * @param {number} options.season - Filter by season year
   */
  async getTopPlayers(teamId, options = {}) {
    try {
      const response = await apiClient.get(
        `/teams/${teamId}/stats/players/top`,
        { params: options }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching top players for team ${teamId}:`, error);
      throw error;
    }
  }

  // ===== PLAYER STATISTICS MANAGEMENT (Coach/Manager only) =====

  /**
   * Record player statistics for a match
   * @param {number} matchId - Match ID
   * @param {Object} statsData - Player statistics data
   * @param {number} statsData.userId - Player user ID
   * @param {number} statsData.teamId - Team ID
   * @param {Object} statsData.statistics - Performance statistics
   * @param {string} statsData.notes - Additional notes (optional)
   */
  async recordPlayerStats(matchId, statsData) {
    try {
      const response = await apiClient.post(
        `/teams/stats/match/${matchId}/player`,
        statsData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error recording player stats for match ${matchId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Update existing player statistics for a match
   * @param {number} matchId - Match ID
   * @param {number} userId - Player user ID
   * @param {Object} statsData - Updated statistics data
   */
  async updatePlayerStats(matchId, userId, statsData) {
    try {
      const response = await apiClient.put(
        `/teams/stats/match/${matchId}/player/${userId}`,
        statsData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating player stats for match ${matchId}, user ${userId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get all player statistics for a specific match
   * @param {number} matchId - Match ID
   */
  async getMatchPlayerStats(matchId) {
    try {
      const response = await apiClient.get(`/teams/stats/match/${matchId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching match player stats for ${matchId}:`, error);
      throw error;
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Check if user can manage team (coach/manager role)
   * @param {Object} user - Current user object
   * @param {number} teamId - Team ID to check
   * @param {Array} userTeams - User's teams array (optional, for optimization)
   */
  canManageTeam(user, teamId, userTeams = null) {
    if (!user || !user.roles) return false;

    // Platform admins can manage all teams
    if (
      user.roles.includes("platform_admin") ||
      user.roles.includes("super_admin")
    ) {
      return true;
    }

    // Organization admins can manage teams in their organization
    if (user.roles.includes("organization_admin")) {
      return true; // Further validation needed based on team's organization
    }

    // Check if user is coach/manager of specific team
    if (userTeams) {
      const teamMembership = userTeams.find((team) => team.id === teamId);
      return (
        teamMembership && ["coach", "manager"].includes(teamMembership.role)
      );
    }

    return false;
  }

  /**
   * Check if user can record player statistics
   * @param {Object} user - Current user object
   * @param {number} teamId - Team ID
   */
  canRecordStats(user, teamId) {
    return this.canManageTeam(user, teamId);
  }

  /**
   * Format team display name
   * @param {Object} team - Team object
   * @returns {string} Formatted team name
   */
  formatTeamName(team) {
    if (!team) return "";
    return `${team.name}${team.organization_name ? ` (${team.organization_name})` : ""}`;
  }

  /**
   * Get team member role badge class
   * @param {string} role - Member role
   * @returns {string} CSS class for role badge
   */
  getRoleBadgeClass(role) {
    const roleClasses = {
      player: "badge-primary",
      captain: "badge-success",
      coach: "badge-warning",
      manager: "badge-danger",
    };
    return roleClasses[role] || "badge-secondary";
  }

  /**
   * Get member status badge class
   * @param {string} status - Member status
   * @returns {string} CSS class for status badge
   */
  getStatusBadgeClass(status) {
    const statusClasses = {
      active: "badge-success",
      inactive: "badge-secondary",
      suspended: "badge-danger",
      pending: "badge-warning",
    };
    return statusClasses[status] || "badge-secondary";
  }

  /**
   * Validate jersey number
   * @param {number} jerseyNumber - Jersey number to validate
   * @param {Array} existingNumbers - Array of existing jersey numbers
   * @returns {Object} Validation result
   */
  validateJerseyNumber(jerseyNumber, existingNumbers = []) {
    const num = parseInt(jerseyNumber);

    if (isNaN(num) || num < 1 || num > 99) {
      return {
        valid: false,
        message: "Jersey number must be between 1 and 99",
      };
    }

    if (existingNumbers.includes(num)) {
      return {
        valid: false,
        message: "Jersey number is already taken",
      };
    }

    return {
      valid: true,
      message: "Jersey number is valid",
    };
  }

  /**
   * Format player statistics for display
   * @param {Object} stats - Player statistics object
   * @param {string} sportType - Type of sport
   * @returns {Object} Formatted statistics
   */
  formatPlayerStats(stats, sportType) {
    if (!stats) return {};

    const formatted = {
      basic: {
        "Games Played": stats.games_played || 0,
        Points: stats.points_scored || 0,
        Assists: stats.assists || 0,
        Minutes: stats.minutes_played || 0,
        Rating: stats.performance_rating
          ? `${stats.performance_rating}/10`
          : "N/A",
      },
    };

    // Add sport-specific statistics
    if (stats.sport_specific_stats) {
      formatted.sportSpecific = this.formatSportSpecificStats(
        stats.sport_specific_stats,
        sportType
      );
    }

    return formatted;
  }

  /**
   * Format sport-specific statistics
   * @param {Object} sportStats - Sport-specific statistics
   * @param {string} sportType - Type of sport
   * @returns {Object} Formatted sport-specific stats
   */
  formatSportSpecificStats(sportStats, sportType) {
    if (!sportStats || typeof sportStats !== "object") return {};

    switch (sportType) {
      case "basketball":
        return {
          "Field Goals": `${sportStats.field_goals_made || 0}/${sportStats.field_goals_attempted || 0}`,
          "3-Pointers": `${sportStats.three_pointers_made || 0}/${sportStats.three_pointers_attempted || 0}`,
          "Free Throws": `${sportStats.free_throws_made || 0}/${sportStats.free_throws_attempted || 0}`,
          "FG%": sportStats.field_goal_percentage
            ? `${sportStats.field_goal_percentage}%`
            : "N/A",
          "3P%": sportStats.three_point_percentage
            ? `${sportStats.three_point_percentage}%`
            : "N/A",
          "FT%": sportStats.free_throw_percentage
            ? `${sportStats.free_throw_percentage}%`
            : "N/A",
        };

      case "soccer":
      case "football":
        return {
          Goals: sportStats.goals || 0,
          Shots: sportStats.shots || 0,
          "Shots on Target": sportStats.shots_on_target || 0,
          "Pass Accuracy": sportStats.pass_accuracy
            ? `${sportStats.pass_accuracy}%`
            : "N/A",
          Tackles: sportStats.tackles || 0,
          Interceptions: sportStats.interceptions || 0,
          Cards: `${sportStats.yellow_cards || 0}Y ${sportStats.red_cards || 0}R`,
        };

      case "cricket":
        return {
          Runs: sportStats.runs || 0,
          "Balls Faced": sportStats.balls_faced || 0,
          "Strike Rate": sportStats.strike_rate
            ? `${sportStats.strike_rate}`
            : "N/A",
          Boundaries: `${sportStats.fours || 0} × 4s, ${sportStats.sixes || 0} × 6s`,
          Wickets: sportStats.wickets_taken || 0,
          "Overs Bowled": sportStats.overs_bowled || 0,
          "Economy Rate": sportStats.economy_rate || "N/A",
        };

      default:
        // Return raw stats for unknown sports
        return sportStats;
    }
  }

  /**
   * Get team roster summary (primary/substitute counts)
   */
  async getTeamRosterSummary(teamId) {
    try {
      const response = await apiClient.get(`/teams/${teamId}/roster`);
      return response.data;
    } catch (error) {
      console.error('Error fetching team roster summary:', error);
      throw error;
    }
  }

  /**
   * Change team member type (primary <-> substitute)
   */
  /**
   * Hand the team to another active member (owner only).
   */
  async transferOwnership(teamId, newOwnerId) {
    const response = await apiClient.post(`/teams/${teamId}/transfer-ownership`, { newOwnerId });
    return response.data;
  }

  async changeMemberType(teamId, userId, memberType, swapUserId = null) {
    try {
      const response = await apiClient.put(`/teams/${teamId}/members/${userId}`, {
        member_type: memberType,
        swapUserId
      });
      return response.data;
    } catch (error) {
      console.error('Error changing member type:', error);
      throw error;
    }
  }

  // ===== ADVANCED SCORING — PLAYER METRICS =====

  /**
   * Record / increment a single advanced-scoring metric for a player in a match.
   * Call this every time the scorer confirms an advanced metric in the live modal.
   *
   * @param {Object} p
   * @param {number} p.userId
   * @param {number} p.matchId
   * @param {number} [p.teamId]
   * @param {number} [p.sportId]
   * @param {string} p.metricKey
   * @param {boolean} p.affectsScore
   * @param {string} [p.clientEventId]  unique per tap; the server ignores a repeat
   *                                    of the same id, so the write can be retried
   * @param {Object} [options]
   * @param {boolean} [options.throwOnError]  rethrow instead of returning null
   */
  async recordPlayerMetric({ userId, matchId, teamId, sportId, metricKey, affectsScore, clientEventId }, options = {}) {
    try {
      const response = await apiClient.post('/teams/stats/player/metric', {
        userId, matchId, teamId, sportId, metricKey, affectsScore,
        ...(clientEventId ? { clientEventId } : {}),
      });
      return response.data;
    } catch (error) {
      if (options.throwOnError) throw error;
      // Non-fatal: stats recording failure should not interrupt the live scoring flow
      console.warn('[teamService] Failed to record player metric:', error?.response?.data || error.message);
      return null;
    }
  }

  /**
   * Undo a previously recorded metric increment for a player in a match.
   * Called when pressing -1 (score correction) to keep player stats accurate.
   *
   * @param {Object} p
   * @param {number} p.userId
   * @param {number} p.matchId
   * @param {string} p.metricKey
   * @param {string} [p.clientEventId]  the clientEventId of the write being undone
   * @param {Object} [options]
   * @param {boolean} [options.throwOnError]  rethrow instead of returning null
   */
  async undoPlayerMetric({ userId, matchId, metricKey, clientEventId }, options = {}) {
    try {
      const response = await apiClient.post('/teams/stats/player/metric/undo', {
        userId, matchId, metricKey,
        ...(clientEventId ? { clientEventId } : {}),
      });
      return response.data;
    } catch (error) {
      if (options.throwOnError) throw error;
      console.warn('[teamService] Failed to undo player metric:', error?.response?.data || error.message);
      return null;
    }
  }

  /**
   * Get aggregated career statistics for a player in a specific sport.
   *
   * @param {number} userId
   * @param {number} sportId
   * @returns {Promise<{ careerTotals, careerHighs, matchesPlayed, ... }>}
   */
  async getPlayerCareerStats(userId, sportId) {
    try {
      const response = await apiClient.get(`/teams/stats/player/${userId}/career`, {
        params: { sportId },
      });
      // Unwrap envelope: { success, data: { ... } }
      return response?.data?.data ?? response?.data ?? {};
    } catch (error) {
      console.error('[teamService] Failed to fetch career stats:', error?.response?.data || error.message);
      throw error;
    }
  }
}

const teamService = new TeamService();
export default teamService;
